import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {Bindings} from "data/constants/bindings";
import {TournamentStatus} from "data/enums/torunaments";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {IPlayerCalculations, IGamePlayer} from "data/utils/player.calculations";
import type {IContestsStore} from "data/stores/contests/contestsStore";

interface IParams {
	playerId: number;
}

export interface IRosterPlayerTournamentInfoController extends ViewController<IParams> {
	get i18n(): ILocalizationStore;

	get pillClass(): string;

	get isTournamentComplete(): boolean;

	get isTournamentLive(): boolean;

	get isHomePlayerSquad(): boolean;

	get homeScore(): number;

	get awayScore(): number;

	get inningState(): string;

	get inningOrdinal(): string;

	get winLoseSymbol(): string;
}

@injectable()
export class RosterPlayerTournamentInfoController implements IRosterPlayerTournamentInfoController {
	@observable private _playerId: number = Number.MAX_SAFE_INTEGER;
	@observable private _gamePlayer: IGamePlayer | undefined = undefined;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.TournamentsStore) private _tournamentsStore: ITournamentsStore,
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations,
		@inject(Bindings.ContestsStore) private readonly _contestsStore: IContestsStore
	) {
		makeAutoObservable(this);
	}

	get pillClass(): string {
		if (this.isTournamentLive) {
			return "live";
		}
		if (this.isTournamentComplete) {
			return "complete";
		}
		return "";
	}

	get isTournamentComplete(): boolean {
		return this.tournament?.status === TournamentStatus.COMPLETE;
	}

	get isTournamentLive(): boolean {
		return this.tournament?.status === TournamentStatus.PLAYING;
	}

	get stats() {
		return this._gamePlayer?.contestStats;
	}

	get inningState(): string {
		const state = this.tournament?.inningState;
		if (!state) {
			return "";
		}
		return this.i18n.t(`game.roster.inning_${state}`);
	}

	get inningOrdinal(): string {
		return this.tournament?.inningOrdinal || "";
	}

	get winLoseSymbol(): "L" | "W" {
		const isHomeWinner = this.homeScore > this.awayScore;

		if (this.isHomePlayerSquad) {
			return isHomeWinner ? "W" : "L";
		}
		return isHomeWinner ? "L" : "W";
	}

	get isHomePlayerSquad() {
		return this.playerWithSquad?.squadId === this.tournament?.homeSquadId;
	}

	get homeScore(): number {
		return this.tournament?.homeSquadScore || 0;
	}

	get awayScore(): number {
		return this.tournament?.awaySquadScore || 0;
	}

	private get playerWithSquad(): IGamePlayer | undefined {
		return this._gamePlayer;
	}

	private get tournament() {
		if (!this.stats?.tournamentId) {
			return;
		}
		return this._tournamentsStore.getTournamentById(this.stats?.tournamentId);
	}

	dispose(): void {
		return;
	}

	init(param: IParams): void {
		this._playerId = param.playerId;
		this._gamePlayer = this._playerCalculations.getGamePlayerById(
			this._playerId,
			this.contestId
		);
	}

	get contestId() {
		return this._contestsStore.currentContest?.id;
	}
	onChange(param: IParams): void {
		this._playerId = param.playerId;
	}
}
